

import React, { ReactNode, useState } from 'react';
import { useGetStudiesQuery, useGetStudyStatsQuery, useGetStudySurveyQuery, useGetStudySurveysQuery } from '../api/studiesAPI';
import { StudyDefinition, SurveyInfo, DailyStatsFile } from '../api/types/studies';
import { ListGroup, Badge, Card, Tab, Tabs, Button, Table, ToggleButton } from "react-bootstrap";
import { LoaderComponent } from '../components/LoaderComponent';
import { LocalizedComponent } from '../components/survey';
import { useSelectedLanguages } from '../hooks/useSelectedLanguages';
import { getLocalizedString } from 'case-web-ui';
import format from 'date-fns/format';
import { VegaLite } from 'react-vega';
import { TopLevelSpec } from 'vega-lite';
import { Icon } from '../components/ui';
import { ExpressionPreviewComponent } from 'ifn-expression-editor';

interface PageProps {

}

interface StudyListProps {
   studies: StudyDefinition[]
}

interface StudyCardProps {
   study: StudyDefinition
   languages: Array<string>
}

interface SurveyListProps {
   study_key: string;
   languages: Array<string>
}


interface SurveyDefProps {
   study_key: string;
   survey_key: string;
   survey_version?: string;
   languages: Array<string>;
   onClose: ()=>void;
}

const saveFile = async (blob: Blob, filename:string) => {
   const a = document.createElement('a');
   a.download = filename;
   a.href = URL.createObjectURL(blob);
   a.addEventListener('click', (e) => {
     setTimeout(() => URL.revokeObjectURL(a.href), 30 * 1000);
   });
   a.click();
 };


const SurveyDef:  React.FC<SurveyDefProps> = (props) => {
   const {data, error, isLoading} = useGetStudySurveyQuery({study:props.study_key, survey: props.survey_key, format:'html'});

   const save = ()=> {
      if(!data) return;
      const b = new Blob([data.html], {type: "text/html"});
      const v = props.survey_version ?? format(new Date(), 'yyyy-MM-dd-HH-mm-ss'); 
      const filename = props.study_key + '_' + props.survey_key + '_' + v + '.html';
      saveFile(b,  filename)
   }

   return <div>
         <Button onClick={()=>props.onClose()} size="sm" className='me-1'>Close</Button>
        { data ? <Button onClick={()=>save()} size="sm" >Save</Button> : '' }
        <LoaderComponent error={error} isLoading={isLoading}/>
        { data ? <iframe srcDoc={data.html} title={ 'Survey' + props.survey_key } style={{ height: "800px", maxWidth:"95%", overflow:"auto", display:"flex", width:"95%" }} className="border m-1 p-1"/> : '' }
   </div>;
}


const SurveyList:  React.FC<SurveyListProps> = (props) => {
   const {data, error, isLoading} = useGetStudySurveysQuery(props.study_key);
   const [ mode, setMode ] = useState<'list'|'html'>('list');
   const [ survey, setSurvey ] = useState<string|undefined>(undefined);
   const [ version, setVersion ] = useState<string|undefined>(undefined);

   if(!data) {
      return <LoaderComponent error={error} isLoading={isLoading}/>
   }

   const showSurvey = (survey: string, version?: string) => {
      setSurvey(survey);
      setVersion(version);
      setMode('html');
   };

   const surveyCard = (survey:SurveyInfo) => {

      // If meta information is available use it
      const version = getLocalizedString(survey.name, '_timestamp');

      return <ListGroup.Item key={survey.surveyKey}>
         <Badge bg="primary">{survey.surveyKey}</Badge>
         <Button variant="info" size="sm" onClick={()=> showSurvey(survey.surveyKey, version)} className="m-1">Show Definition</Button>
         <LocalizedComponent contents={survey.name} languages={props.languages}/>
         <LocalizedComponent contents={survey.description} languages={props.languages} />
      </ListGroup.Item>
   };

   return(
      <Card className='mx-2'>
         <Card.Header as="h5">Surveys</Card.Header>
         { mode === 'list' ? <ListGroup>{data.surveys.map(surveyCard)}</ListGroup> : '' }
         { mode === 'html' && survey ? <Card.Body><SurveyDef study_key={props.study_key} survey_key={survey} survey_version={version} languages={props.languages} onClose={()=>setMode('list')} /></Card.Body> : '' }
      </Card>
   );
}

const StudyCard:  React.FC<StudyCardProps> = (props) => {
  
   const study = props.study;

   return (
         <ListGroup.Item key={study.key} className="py-1">
         <Card>
            <Card.Header as="h5">{study.key}
            <Badge bg="primary" className="ms-1">{study.status}</Badge>
            { study.props.systemDefaultStudy ? <Badge bg="warning" className="ms-1">System default</Badge> : ''}
            </Card.Header>
            <Card.Body>
            <Tabs defaultActiveKey="profile" id="uncontrolled-tab-example" className="mb-3">
               <Tab eventKey="infos" title="Study Infos">
                     Name <LocalizedComponent contents={study.props.name} languages={props.languages}/>
                     Description <LocalizedComponent contents={study.props.description} languages={props.languages}/>
                  <ul className="study-stats">
                     <li>Participants <Badge pill bg="info">{study.stats.participantCount}</Badge></li>
                     <li>Responses <Badge pill bg="info">{study.stats.responseCount}</Badge></li>
                     <li>Temp. Participations <Badge pill bg="info">{study.stats.tempParticipantCount}</Badge></li>
                  </ul>
               </Tab>
               <Tab eventKey="rules" title="Rules">
                  { study.rules ?
                     <ListGroup>
                        { study.rules.map( (rule, index)=> <ListGroup.Item key={index}><ExpressionPreviewComponent exp={rule}/></ListGroup.Item> ) }
                     </ListGroup>
                     : 'No rule defined'
                  }
               </Tab>
               <Tab eventKey="surveys" title="Surveys">
                  <SurveyList study_key={study.key} languages={props.languages}/>
               </Tab>
               <Tab eventKey="stats" title="Stats">
                  <SurveyStat study_key={study.key}/>
               </Tab>
            </Tabs>            
            </Card.Body>
         </Card>
         </ListGroup.Item>
   )
}

interface SurveyStatProps {
   study_key: string;
}

const SurveyStat:  React.FC<SurveyStatProps> = (props) => {

   const {data, error, isLoading, refetch} = useGetStudyStatsQuery(props.study_key);
   
   return ( 
      <React.Fragment>
         <h1>Survey Responses Statistics</h1>
        <LoaderComponent isLoading={isLoading} error={error} />
        { data && data.error ? data.error : ''}
        { data && data.data ? <SurveyStatView data={data.data} onRefresh={ () => refetch() }/> : ''}
      </React.Fragment>
   )
};

interface SurveyStatViewProps {
   data: DailyStatsFile
   onRefresh: ()=>void
}

interface StatRow {
  date: string;
  value: number;
  survey:string;
}

class StudyStat {

   surveys: string[];
   stats: Record<string, Record<string,number>>;
   
   constructor(data: DailyStatsFile) {
      this.surveys = [];
      this.stats = data.dates;

      const ss = new Set<string>();
      Object.entries(data.dates).forEach(entry => {
         Object.entries(entry[1]).forEach( (e) => {
            ss.add(e[0]);
         });
      });
      this.surveys = Array.from(ss);
      
      console.log('stats', this);
   }

   getValues(): Array<StatRow> {
      const data : Array<StatRow> = [];
      Object.entries(this.stats).forEach(entry => {
         const date = entry[0];
         Object.entries(entry[1]).forEach( (e) => {
            data.push({'date': date, value: e[1], survey: e[0]});
         });
      });
     
      return data;
   }

}

const SurveyStatView :  React.FC<SurveyStatViewProps> = (props) => {

   const [stats, ] = useState<StudyStat>(new StudyStat(props.data));
   const [surveys, setSurveys] = useState<string[]>(stats.surveys);

   const showValues =  (values: Record<string, Record<string,number>>) => {
      const rows : ReactNode[] = [];
      
      Object.entries(values).forEach(entry => {

         const date = entry[0];
         const vv = entry[1];
         const cols: ReactNode[] = [];
         cols.push(<td key="date">{date}</td>);

         surveys.forEach((k, i) => {
            const v = vv[k] ?? '';
            cols.push(<td key={i}>{v}</td> );
         });
         
         rows.push(<tr key={date}>{ cols }</tr>);
      });
      
      return rows;
   }

   const handleChangeSurvey = (state:boolean, survey: string) => {
      const ss = new Set<string>(surveys);
      if(state) {
         ss.add(survey);
      } else {
         ss.delete(survey);
      }
      setSurveys(Array.from(ss.values()));
   }

   const vegaLiteSpec: TopLevelSpec = {
      $schema: 'https://vega.github.io/schema/vega-lite/v5.json',
      width:700,
      height: 500,
      data: {
        values: stats.getValues()
      },
      transform: [
         {filter: { field: 'survey', oneOf: surveys}},
      ],
      mark: 'line',
      encoding: {
        x: {field: 'date', type: 'temporal', timeUnit: "yearmonthdate"},
        y: {field: 'value', type: 'quantitative'},
        color: {field: 'survey', type:"nominal"}
      }
    };

   return <div>
            <div className='row'>
               <div className="col-11">
               { stats.surveys.map(survey => {
                  return <ToggleButton
                  id={"toggle-check" + survey} 
                  key={survey}
                  type="checkbox"
                  variant="outline-primary"
                  size="sm"
                  className='me-1'
                  checked={surveys.includes(survey)}
                  value={survey}
                  onChange={(e) => handleChangeSurvey(e.currentTarget.checked, survey)}
                >
                  {survey}
               </ToggleButton>
                  }) 
               }
               
               </div>
               <div className="col-1"> 
                  <Button onClick={()=>props.onRefresh()} size="sm" variant='default'><Icon name="sync"/> refresh</Button>
               </div>
            </div>
            <div className="row">
               <div className="col-4">
                  <Table>
                     <thead>
                        <tr>
                           <td>Date</td>
                           { surveys.map((k) => <td key={k}>{k}</td>)}
                        </tr>
                     </thead>
                     <tbody>
                        { showValues(stats.stats) }
                     </tbody>
                  </Table>
               </div>
               <div className='col-8'>
                  <VegaLite spec={vegaLiteSpec} />
               </div>
            </div>
         </div>

};


const StudyList:  React.FC<StudyListProps> = (props) => {

   const languages = useSelectedLanguages();

   return <ListGroup>
      { props.studies.map((study)=> <StudyCard study={study} languages={languages} key={study.id} />) }
   </ListGroup>

};


export const StudiesPage: React.FC<PageProps> = (props) => {
   
   const {data, error, isLoading} = useGetStudiesQuery();
   
   return ( 
      <React.Fragment>
         <h1>Platform studies</h1>
        <LoaderComponent isLoading={isLoading} error={error} />
        { data ? <StudyList studies={data.studies}/> : ''}
      </React.Fragment>
   )
};

